import {
  Box,
  Text,
  HStack,
  VStack,
  Flex,
  Spinner,
  Divider,
  useDisclosure,
  useToast,
  Tooltip,
} from '@chakra-ui/react';
import {
  IoChevronDown,
  IoChevronUp,
  IoCheckmarkCircle,
  IoAddCircleOutline,
} from 'react-icons/io5';
import { useState, useRef, useEffect } from 'react';
import { useSession } from 'next-auth/react';
import { useRouter } from 'next/router';
import { motion, AnimatePresence } from 'framer-motion';
import { TeamEntry } from '../hooks/useProtectedSession';
import CreateTeamModal from './CreateTeamModal';

interface TeamSwitcherDropdownProps {
  teams: TeamEntry[];
  activeTeamName: string;
  activeTeamId?: string;
  accessToken: string;
}

const getInitials = (name: string) => {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();
};

const TeamSwitcherDropdown = ({ teams, activeTeamName, activeTeamId, accessToken }: TeamSwitcherDropdownProps) => {
  // update() was added in next-auth v4.22 — guard before calling
  const { update } = useSession() as any;
  const router = useRouter();
  const toast = useToast();

  const refreshSession = async () => {
    if (typeof update === 'function') {
      await update();
    } else {
      router.reload();
    }
  };
  const api_url = process.env.NEXT_PUBLIC_API_URL;

  // When the backend hasn't returned the teams array yet, synthesize a single
  // entry from the current active team so the dropdown always renders.
  const displayTeams: TeamEntry[] =
    teams.length > 0
      ? teams
      : activeTeamName
      ? [{ _id: activeTeamId || '', name: activeTeamName, isActive: true, role: '', owner: false }]
      : [];

  const [isOpen, setIsOpen] = useState(false);
  const [switchingTeamId, setSwitchingTeamId] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const {
    isOpen: isCreateOpen,
    onOpen: onCreateOpen,
    onClose: onCreateClose,
  } = useDisclosure();

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSwitch = async (team: TeamEntry) => {
    if (team.isActive || switchingTeamId) return;

    setSwitchingTeamId(team._id);
    setIsOpen(false);

    try {
      const res = await fetch(`${api_url}/users/me/switch-team/${team._id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      if (!res.ok) {
        let msg = 'Team-Wechsel fehlgeschlagen.';
        try {
          const j = await res.json();
          if (j?.message) msg = String(j.message);
        } catch (_) {}
        toast({
          title: 'Fehler',
          description: msg,
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'bottom-left',
        });
        return;
      }

      await refreshSession();
      toast({
        title: 'Team gewechselt',
        description: `Du arbeitest jetzt mit "${team.name}".`,
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'bottom-left',
      });
    } catch (_) {
      toast({
        title: 'Fehler',
        description: 'Netzwerkfehler beim Team-Wechsel.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'bottom-left',
      });
    } finally {
      setSwitchingTeamId(null);
    }
  };

  const isSwitching = switchingTeamId !== null;

  const trigger = (
    // Fixed height - never changes between collapsed and expanded sidebar state.
    // The team name is absolutely positioned (same pattern as .side-menu-user-info)
    // so it never contributes to the element's height.
    <Box
      onClick={() => setIsOpen((v) => !v)}
      cursor="pointer"
      h="44px"
      position="relative"
      borderRadius="lg"
      border="1px solid"
      borderColor={isOpen ? 'rekening.500' : 'gray.200'}
      bg={isOpen ? 'green.50' : 'gray.50'}
      transition="all 0.2s"
      _hover={{ borderColor: 'rekening.500', bg: 'green.50' }}
    >
      {/* Badge — always visible, fixed left position */}
      <Flex
        position="absolute"
        left="10px"
        top="50%"
        transform="translateY(-50%)"
        align="center"
        justify="center"
        w={7}
        h={7}
        borderRadius="md"
        bg="rekening.500"
        flexShrink={0}
      >
        {isSwitching ? (
          <Spinner size="xs" color="white" />
        ) : (
          <Text fontSize="10px" fontWeight="bold" color="white" lineHeight={1}>
            {getInitials(activeTeamName || 'T')}
          </Text>
        )}
      </Flex>

      {/* Team name + chevron — absolutely positioned, opacity-based show/hide.
          Uses the same pattern as .side-menu-user-info so it never affects height. */}
      <Box
        className="side-menu-team-text"
        position="absolute"
        left="44px"
        right="10px"
        top="50%"
        transform="translateY(-50%)"
        display="flex"
        alignItems="center"
        justifyContent="space-between"
      >
        <VStack spacing={0} align="start" overflow="hidden" flex={1} mr={2}>
          <Text fontSize="xs" color="gray.400" fontWeight="bold" textTransform="uppercase" lineHeight={1} whiteSpace="nowrap">
            Team
          </Text>
          <Text fontSize="sm" fontWeight="semibold" color="gray.800" whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis" maxW="140px">
            {activeTeamName}
          </Text>
        </VStack>
        {isOpen ? (
          <IoChevronUp size={14} color="#718096" />
        ) : (
          <IoChevronDown size={14} color="#718096" />
        )}
      </Box>
    </Box>
  );

  return (
    <Box ref={dropdownRef} position="relative">
      {/* Tooltip only visible when collapsed */}
      <Tooltip label={activeTeamName} placement="right" hasArrow openDelay={300}>
        {trigger}
      </Tooltip>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={{ duration: 0.15 }}
            style={{
              position: 'absolute',
              top: 'calc(100% + 6px)',
              left: 0,
              width: '240px',
              zIndex: 2000,
            }}
          >
            <Box
              bg="white"
              borderRadius="xl"
              boxShadow="xl"
              border="1px solid"
              borderColor="gray.100"
              overflow="hidden"
              py={1}
            >
              {/* Section header */}
              <Text
                fontSize="10px"
                fontWeight="bold"
                textTransform="uppercase"
                color="gray.400"
                px={3}
                pt={2}
                pb={1}
                letterSpacing="wider"
              >
                Deine Teams
              </Text>

              {/* Team list */}
              {displayTeams.length === 0 ? (
                <Box px={3} py={2}>
                  <Text fontSize="sm" color="gray.400">Keine Teams gefunden</Text>
                </Box>
              ) : (
                displayTeams.map((team) => {
                  const isSwitchingThis = switchingTeamId === team._id;
                  return (
                    <Box
                      key={team._id}
                      px={3}
                      py="10px"
                      cursor={team.isActive ? 'default' : 'pointer'}
                      bg={team.isActive ? 'green.50' : 'transparent'}
                      _hover={team.isActive ? {} : { bg: 'gray.50' }}
                      onClick={() => handleSwitch(team)}
                      transition="background 0.15s"
                    >
                      <HStack spacing={3}>
                        <Box
                          w={8}
                          h={8}
                          borderRadius="md"
                          bg={team.isActive ? 'rekening.500' : 'gray.100'}
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          flexShrink={0}
                        >
                          {isSwitchingThis ? (
                            <Spinner size="xs" color={team.isActive ? 'white' : 'gray.500'} />
                          ) : (
                            <Text
                              fontSize="11px"
                              fontWeight="bold"
                              color={team.isActive ? 'white' : 'gray.500'}
                              lineHeight={1}
                            >
                              {getInitials(team.name)}
                            </Text>
                          )}
                        </Box>
                        <VStack spacing={0} align="start" flex={1} overflow="hidden">
                          <Text
                            fontSize="sm"
                            fontWeight={team.isActive ? 'semibold' : 'medium'}
                            color={team.isActive ? 'rekening.600' : 'gray.700'}
                            noOfLines={1}
                          >
                            {team.name}
                          </Text>
                          <HStack spacing={1}>
                            <Text fontSize="10px" color="gray.400" textTransform="uppercase" fontWeight="medium">
                              {team.role}
                            </Text>
                            {team.owner && (
                              <>
                                <Text fontSize="10px" color="gray.300">·</Text>
                                <Text fontSize="10px" color="gray.400">Eigentümer</Text>
                              </>
                            )}
                          </HStack>
                        </VStack>
                        {team.isActive && (
                          <IoCheckmarkCircle size={16} color="#1a5f3f" />
                        )}
                      </HStack>
                    </Box>
                  );
                })
              )}

              <Divider my={1} borderColor="gray.100" />

              {/* Create new team */}
              <Box
                px={3}
                py="10px"
                cursor="pointer"
                _hover={{ bg: 'gray.50' }}
                onClick={() => {
                  setIsOpen(false);
                  onCreateOpen();
                }}
                transition="background 0.15s"
              >
                <HStack spacing={3}>
                  <Box
                    w={8}
                    h={8}
                    borderRadius="md"
                    border="1.5px dashed"
                    borderColor="gray.300"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    flexShrink={0}
                  >
                    <IoAddCircleOutline size={16} color="#718096" />
                  </Box>
                  <Text fontSize="sm" fontWeight="medium" color="gray.600">
                    Neues Team erstellen
                  </Text>
                </HStack>
              </Box>
            </Box>
          </motion.div>
        )}
      </AnimatePresence>

      <CreateTeamModal
        isOpen={isCreateOpen}
        onClose={onCreateClose}
        accessToken={accessToken}
      />
    </Box>
  );
};

export default TeamSwitcherDropdown;
